import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useContact } from '../contexts/ContactContext';
import buildingImg from '../assets/building.png';
import SEO from '../components/SEO';

const Contact = () => {
  const { contactInfo } = useContact();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const { error } = await supabase.from('requests').insert([{
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        type: 'Contact Form',
        message: `Subject: ${formData.subject}\n\n${formData.message}`
      }]);
      
      if (error) throw error;
      setSuccess(true);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      console.error('Error submitting form:', err);
      alert('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#041424] min-h-screen w-full font-sans text-white">
      <SEO 
        title="Contact Us | HBB0011 REALTY"
        description="Get in touch with HBB0011 REALTY today to discuss your next real estate investment or architectural project."
        keywords="contact HBB0011 REALTY, real estate agents, luxury property inquiry"
      />
      
      {/* 1. Hero Section */}
      <header className="relative w-full pt-28 pb-16 md:pt-32 md:pb-24 lg:min-h-[60vh] flex items-center overflow-hidden">
        {/* Background Image Blend */}
        <div className="absolute inset-0 z-0 flex justify-end">
          <div className="w-full lg:w-1/2 h-full relative">
            <img 
              src={buildingImg} 
              alt="Luxury real estate" 
              className="w-full h-full object-cover opacity-30 lg:opacity-50 mix-blend-luminosity"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#041424] via-[#041424]/90 lg:via-[#041424]/40 to-transparent"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-[#041424] via-transparent to-transparent"></div>
          </div>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="w-full lg:w-1/2 space-y-4 sm:space-y-6 text-center lg:text-left">
            {/* Breadcrumb */}
            <div className="flex items-center justify-center lg:justify-start gap-2 text-xs sm:text-sm font-bold tracking-widest mb-2 sm:mb-4">
              <Link to="/" className="text-white hover:text-[#D4AF37] transition-colors uppercase">Home</Link>
              <span className="text-gray-400">&gt;</span>
              <span className="text-[#D4AF37] uppercase">Contact Us</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-2 tracking-tight uppercase">
              Contact Us
            </h1>
            
            <h2 className="text-xl sm:text-2xl md:text-3xl text-[#D4AF37] font-medium leading-tight mb-4 sm:mb-6 uppercase">
              We're here to help <br className="hidden sm:block"/>
              you build your <br className="hidden sm:block"/>
              dreams.
            </h2>
            
            <p className="text-gray-400 max-w-md mx-auto lg:mx-0 text-sm sm:text-base leading-relaxed">
              Whether you have a question, need more information, or want to explore investment opportunities, our team is ready to assist you.
            </p>
          </div>
        </div>
      </header>

      {/* 2. Contact Info Bar */}
      <section className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 sm:-mt-12 lg:-mt-16 mb-16 lg:mb-24">
        <div className="border border-gray-800 rounded-sm bg-[#041424]/90 backdrop-blur-md p-6 lg:p-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-4 divide-y sm:divide-y-0 sm:divide-x divide-gray-800">
            
            <div className="flex items-center gap-4 pt-4 sm:pt-0 lg:px-4">
              <div className="w-12 h-12 rounded-full border border-[#D4AF37]/50 flex items-center justify-center shrink-0">
                <span className="material-symbols-outlined text-[#D4AF37]">call</span>
              </div>
              <div>
                <h4 className="text-white text-sm font-bold mb-1">Call Us</h4>
                <p className="text-gray-400 text-xs">{contactInfo?.phone}</p>
              </div>
            </div>

            <div className="flex items-center gap-4 pt-4 sm:pt-0 lg:px-4">
              <div className="w-12 h-12 rounded-full border border-[#D4AF37]/50 flex items-center justify-center shrink-0">
                <span className="material-symbols-outlined text-[#D4AF37]">mail</span>
              </div>
              <div>
                <h4 className="text-white text-sm font-bold mb-1">Email Us</h4>
                <p className="text-gray-400 text-xs">{contactInfo?.email}</p>
              </div>
            </div>

            <div className="flex items-center gap-4 pt-4 sm:pt-0 lg:px-4">
              <div className="w-12 h-12 rounded-full border border-[#D4AF37]/50 flex items-center justify-center shrink-0">
                <span className="material-symbols-outlined text-[#D4AF37]">language</span>
              </div>
              <div>
                <h4 className="text-white text-sm font-bold mb-1">Visit Our Website</h4>
                <p className="text-gray-400 text-xs">www.hbb0011realty.com</p>
              </div>
            </div>

            <div className="flex items-center gap-4 pt-4 sm:pt-0 lg:px-4">
              <div className="w-12 h-12 rounded-full border border-[#D4AF37]/50 flex items-center justify-center shrink-0">
                <span className="material-symbols-outlined text-[#D4AF37]">schedule</span>
              </div>
              <div>
                <h4 className="text-white text-sm font-bold mb-1">Business Hours</h4>
                <p className="text-gray-400 text-xs">{contactInfo?.business_hours}</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      <main className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-20 lg:space-y-24 pb-20">
        
        {/* 3. Form & Offices Split */}
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          
          {/* Left: Message Form */}
          <div className="lg:col-span-7 border border-gray-800 rounded-sm p-6 sm:p-8 md:p-10 bg-[#06121e]">
            <h2 className="text-2xl text-white font-bold mb-2 uppercase tracking-wide">Send Us a Message</h2>
            <p className="text-gray-400 text-sm mb-8">Fill in the form below and our team will get back to you shortly.</p>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {success && (
                <div className="bg-green-500/20 border border-green-500/50 text-green-400 p-4 rounded-sm text-sm">
                  Your message has been sent successfully! Our team will contact you soon.
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-gray-300 text-xs font-bold uppercase tracking-wider">Full Name</label>
                  <input type="text" name="name" value={formData.name} onChange={handleInputChange} required className="w-full bg-[#041424] border border-gray-700 rounded-sm p-4 text-white text-sm focus:border-[#D4AF37] focus:outline-none transition-colors" />
                </div>
                <div className="space-y-2">
                  <label className="text-gray-300 text-xs font-bold uppercase tracking-wider">Email Address</label>
                  <input type="email" name="email" value={formData.email} onChange={handleInputChange} required className="w-full bg-[#041424] border border-gray-700 rounded-sm p-4 text-white text-sm focus:border-[#D4AF37] focus:outline-none transition-colors" />
                </div>
                <div className="space-y-2">
                  <label className="text-gray-300 text-xs font-bold uppercase tracking-wider">Phone Number</label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} required className="w-full bg-[#041424] border border-gray-700 rounded-sm p-4 text-white text-sm focus:border-[#D4AF37] focus:outline-none transition-colors" />
                </div>
                <div className="space-y-2">
                  <label className="text-gray-300 text-xs font-bold uppercase tracking-wider">Subject</label>
                  <input type="text" name="subject" value={formData.subject} onChange={handleInputChange} className="w-full bg-[#041424] border border-gray-700 rounded-sm p-4 text-white text-sm focus:border-[#D4AF37] focus:outline-none transition-colors" />
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="text-gray-300 text-xs font-bold uppercase tracking-wider">Message / Enquiry</label>
                <textarea rows="5" name="message" value={formData.message} onChange={handleInputChange} required className="w-full bg-[#041424] border border-gray-700 rounded-sm p-4 text-white text-sm focus:border-[#D4AF37] focus:outline-none transition-colors resize-none"></textarea>
              </div>

              <div className="flex items-start gap-3 pt-2">
                <input type="checkbox" id="terms" required className="mt-1 w-4 h-4 rounded-sm border-gray-700 bg-[#041424] text-[#D4AF37] focus:ring-[#D4AF37] focus:ring-offset-[#041424]" />
                <label htmlFor="terms" className="text-gray-400 text-xs leading-relaxed">
                  I agree to the <Link to="/privacy" className="text-[#D4AF37] hover:underline">Privacy Policy</Link> and <Link to="/terms" className="text-[#D4AF37] hover:underline">Terms & Conditions</Link>.
                </label>
              </div>

              <button type="submit" disabled={loading} className={`w-full font-bold text-xs uppercase tracking-widest py-5 rounded-sm transition-colors flex items-center justify-center gap-2 mt-4 ${loading ? 'bg-gray-600 text-gray-400 cursor-not-allowed' : 'bg-[#D4AF37] text-[#041424] hover:bg-white'}`}>
                {loading ? (
                  <>
                    <span className="material-symbols-outlined animate-spin text-base">sync</span> Processing...
                  </>
                ) : (
                  <>
                    Send Message <span className="material-symbols-outlined text-base">arrow_forward</span>
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Right: Offices List */}
          <div className="lg:col-span-5 border border-gray-800 rounded-sm p-6 sm:p-8 bg-[#06121e]">
            <h2 className="text-2xl text-white font-bold mb-8 uppercase tracking-wide">Our Offices</h2>
            
            <div className="space-y-6">
              {/* Office 1 */}
              <div className="flex items-center gap-4 group cursor-pointer border-b border-gray-800 pb-6">
                <div className="w-24 h-16 rounded-sm bg-[#041424] border border-gray-700 shrink-0"></div>
                <div className="flex-grow">
                  <h4 className="text-white text-sm font-bold uppercase tracking-wider mb-1">Corporate Office - Kochi</h4>
                  <p className="text-gray-400 text-xs leading-relaxed"></p>
                </div>
                <span className="material-symbols-outlined text-[#D4AF37] opacity-50 group-hover:opacity-100 transition-opacity">location_on</span>
              </div>

              {/* Office 2 */}
              <div className="flex items-center gap-4 group cursor-pointer border-b border-gray-800 pb-6">
                <div className="w-24 h-16 rounded-sm bg-[#041424] border border-gray-700 shrink-0"></div>
                <div className="flex-grow">
                  <h4 className="text-white text-sm font-bold uppercase tracking-wider mb-1">Sales Gallery - Kochi</h4>
                  <p className="text-gray-400 text-xs leading-relaxed"></p>
                </div>
                <span className="material-symbols-outlined text-[#D4AF37] opacity-50 group-hover:opacity-100 transition-opacity">location_on</span>
              </div>

              {/* Office 3 */}
              <div className="flex items-center gap-4 group cursor-pointer border-b border-gray-800 pb-6">
                <div className="w-24 h-16 rounded-sm bg-[#041424] border border-gray-700 shrink-0"></div>
                <div className="flex-grow">
                  <h4 className="text-white text-sm font-bold uppercase tracking-wider mb-1">Branch Office - Bangalore</h4>
                  <p className="text-gray-400 text-xs leading-relaxed"></p>
                </div>
                <span className="material-symbols-outlined text-[#D4AF37] opacity-50 group-hover:opacity-100 transition-opacity">location_on</span>
              </div>

              {/* Office 4 */}
              <div className="flex items-center gap-4 group cursor-pointer">
                <div className="w-24 h-16 rounded-sm bg-[#041424] border border-gray-700 shrink-0"></div>
                <div className="flex-grow">
                  <h4 className="text-white text-sm font-bold uppercase tracking-wider mb-1">Branch Office - Hyderabad</h4>
                  <p className="text-gray-400 text-xs leading-relaxed"></p>
                </div>
                <span className="material-symbols-outlined text-[#D4AF37] opacity-50 group-hover:opacity-100 transition-opacity">location_on</span>
              </div>
            </div>
          </div>
        </section>

        {/* 4. Why Choose Us */}
        <section className="pt-10">
          <h2 className="text-2xl sm:text-3xl text-white font-bold mb-12 uppercase tracking-wide text-center lg:text-left">Why Choose HBB0011 Realty?</h2>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 sm:gap-8">
            <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
              <span className="material-symbols-outlined text-[#D4AF37] text-4xl sm:text-5xl font-light mb-4">stars</span>
              <h4 className="text-white font-bold text-xs uppercase tracking-widest mb-2">Trust & Transparency</h4>
              <p className="text-gray-400 text-[11px] leading-relaxed">We believe in honest dealings and complete transparency in every transaction.</p>
            </div>
            <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
              <span className="material-symbols-outlined text-[#D4AF37] text-4xl sm:text-5xl font-light mb-4">apartment</span>
              <h4 className="text-white font-bold text-xs uppercase tracking-widest mb-2">Premium Quality</h4>
              <p className="text-gray-400 text-[11px] leading-relaxed">We deliver world-class projects with superior construction quality and luxurious design.</p>
            </div>
            <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
              <span className="material-symbols-outlined text-[#D4AF37] text-4xl sm:text-5xl font-light mb-4">location_on</span>
              <h4 className="text-white font-bold text-xs uppercase tracking-widest mb-2">Prime Locations</h4>
              <p className="text-gray-400 text-[11px] leading-relaxed">Our projects are located in high-growth areas with excellent connectivity and appreciation potential.</p>
            </div>
            <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
              <span className="material-symbols-outlined text-[#D4AF37] text-4xl sm:text-5xl font-light mb-4">group</span>
              <h4 className="text-white font-bold text-xs uppercase tracking-widest mb-2">Customer First</h4>
              <p className="text-gray-400 text-[11px] leading-relaxed">Our customers are at the heart of everything we do. We build relationships that last.</p>
            </div>
            <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
              <span className="material-symbols-outlined text-[#D4AF37] text-4xl sm:text-5xl font-light mb-4">verified_user</span>
              <h4 className="text-white font-bold text-xs uppercase tracking-widest mb-2">Timely Delivery</h4>
              <p className="text-gray-400 text-[11px] leading-relaxed">We are committed to delivering every project on time, every time.</p>
            </div>
            <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
              <span className="material-symbols-outlined text-[#D4AF37] text-4xl sm:text-5xl font-light mb-4">headset_mic</span>
              <h4 className="text-white font-bold text-xs uppercase tracking-widest mb-2">Expert Support</h4>
              <p className="text-gray-400 text-[11px] leading-relaxed">Our experienced team is always here to guide you at every step of your real estate journey.</p>
            </div>
          </div>
        </section>

        {/* 5. Map & Connections */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-8 pt-10">
          
          {/* Map Area */}
          <div className="lg:col-span-2 border border-gray-800 rounded-sm bg-[#06121e] relative overflow-hidden h-[400px] sm:h-[450px]">
            <h2 className="absolute top-6 left-6 z-20 text-white font-bold uppercase tracking-wider">Find Us Here</h2>
            <div className="absolute inset-0 bg-[#020b14] flex items-center justify-center">
              <p className="text-gray-600 text-xs uppercase tracking-widest">No Map Available</p>
            </div>
          </div>

          {/* Connect / Book */}
          <div className="lg:col-span-1 flex flex-col gap-8">
            <div className="border border-gray-800 rounded-sm p-8 bg-[#06121e] h-1/2 flex flex-col justify-center">
              <h2 className="text-lg text-white font-bold mb-2 uppercase tracking-wide">Connect With Us</h2>
              <p className="text-gray-400 text-xs leading-relaxed mb-6">Follow us on social media for the latest updates, projects, and real estate insights.</p>
              <div className="flex gap-4">
                <Link to="#" className="w-10 h-10 rounded-full border border-gray-600 flex items-center justify-center text-white hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all">
                  <span className="font-bold text-sm">f</span>
                </Link>
                <Link to="#" className="w-10 h-10 rounded-full border border-gray-600 flex items-center justify-center text-white hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all">
                  <span className="material-symbols-outlined text-sm">photo_camera</span> {/* Insta placeholder */}
                </Link>
                <Link to="#" className="w-10 h-10 rounded-full border border-gray-600 flex items-center justify-center text-white hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all">
                  <span className="font-bold text-sm">in</span>
                </Link>
                <Link to="#" className="w-10 h-10 rounded-full border border-gray-600 flex items-center justify-center text-white hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all">
                  <span className="material-symbols-outlined text-sm">play_arrow</span> {/* YT placeholder */}
                </Link>
              </div>
            </div>

            <div className="border border-gray-800 rounded-sm p-8 bg-[#06121e] h-1/2 flex flex-col justify-center">
              <h2 className="text-lg text-white font-bold mb-2 uppercase tracking-wide">Book a Site Visit</h2>
              <p className="text-gray-400 text-xs leading-relaxed mb-6">Experience our projects in person.</p>
              <button className="bg-[#D4AF37] text-[#041424] font-bold text-xs uppercase tracking-widest py-4 rounded-sm hover:bg-white transition-colors flex items-center justify-center gap-2 w-full">
                <span className="material-symbols-outlined text-base">event</span>
                Book Now <span className="material-symbols-outlined text-base">arrow_forward</span>
              </button>
            </div>
          </div>

        </section>
      </main>

      {/* 6. Pre-Footer / Feature Banner */}
      <section className="border-t border-b border-gray-800 bg-[#041424] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 divide-y sm:divide-y-0 sm:divide-x divide-gray-800">
          
          <div className="flex items-start gap-4 pt-4 sm:pt-0 lg:px-4">
            <span className="material-symbols-outlined text-[#D4AF37] text-3xl font-light">headset_mic</span>
            <div>
              <h4 className="text-white text-xs font-bold uppercase tracking-widest mb-1">Talk to an Expert</h4>
              <p className="text-gray-400 text-[10px] leading-relaxed mb-2">Get personalized advice from our real estate experts.</p>
              <a href="tel:+919916710011" className="text-[#D4AF37] text-[10px] hover:underline">Call us now<br/>+91 99167 10011</a>
            </div>
          </div>

          <div className="flex items-start gap-4 pt-4 sm:pt-0 lg:px-4">
            <span className="material-symbols-outlined text-[#D4AF37] text-3xl font-light">calendar_month</span>
            <div>
              <h4 className="text-white text-xs font-bold uppercase tracking-widest mb-1">Book a Site Visit</h4>
              <p className="text-gray-400 text-[10px] leading-relaxed mb-2">Visit our projects and experience our quality firsthand.</p>
              <Link to="/book-visit" className="text-[#D4AF37] text-[10px] hover:underline">Book your visit today</Link>
            </div>
          </div>

          <div className="flex items-start gap-4 pt-4 sm:pt-0 lg:px-4">
            <span className="material-symbols-outlined text-[#D4AF37] text-3xl font-light">menu_book</span>
            <div>
              <h4 className="text-white text-xs font-bold uppercase tracking-widest mb-1">Download Brochure</h4>
              <p className="text-gray-400 text-[10px] leading-relaxed mb-2">Download our project brochures and company profile.</p>
              <Link to="/brochure" className="text-[#D4AF37] text-[10px] hover:underline">Get detailed information</Link>
            </div>
          </div>

          <div className="flex items-start gap-4 pt-4 sm:pt-0 lg:px-4">
            <span className="material-symbols-outlined text-[#D4AF37] text-3xl font-light">handshake</span>
            <div>
              <h4 className="text-white text-xs font-bold uppercase tracking-widest mb-1">Invest with Confidence</h4>
              <p className="text-gray-400 text-[10px] leading-relaxed mb-2">Trusted by thousands of clients. Let us help you build your dream investment.</p>
            </div>
          </div>

        </div>
      </section>

    </div>
  );
};

export default Contact;